import BoundingClientRectTool from "../tools/BoundingClientRectTool";
import DOMAttributeTool from "../tools/DOMAttributeTool";
import RecordAttributeTool from "../tools/RecordAttributeTool";
import RelativePositionTool from "../tools/RelativePositionTool";
import LerpTool from "../tools/LerpTool";
import TransformNullifyTool from "../tools/TransformNullifyTool";
import UnitParserTool from "../tools/UnitParserTool";
import AdaptiveLerpTool from "../tools/AdaptiveLerpTool";
import OriginParserTool from "../tools/OriginParserTool";
import ColorParserTool from "../tools/ColorParserTool";
import EasingFunctionTool from "../tools/EasingFunctionTool";
import MagneticPullTool from "../tools/MagneticPullTool";
import LerpColorTool from "../tools/LerpColorTool";
import LerpVector2Tool from "../tools/LerpVector2Tool";
import TransformScaleParserTool from "../tools/TransformScaleParserTool";
import { SplitOptionsParserTool } from "../tools/SplitOptionsParserTool";
import RuleParserTool from "../tools/RuleParserTool";
import { ValidationTool } from "../tools/ValidationTool";
import { frameDOM } from "../utils/frame-dom";
import { styleTxn } from "../utils/style-txn";

/**
 * Container holding references to all essential tools used within the library.
 * Dependency injection.
 */
export interface StringToolsContainer {
  /** Tool for reading DOM attributes (including data-*). */
  domAttribute: DOMAttributeTool;

  /** Tool for reading attributes from a plain JS object or dataset. */
  recordAttribute: RecordAttributeTool;

  /** Tool for calculating the relative position between two elements. */
  relativePosition: RelativePositionTool;

  /** Tool that nullifies the effect of CSS transform matrix. */
  transformNullify: TransformNullifyTool;

  /** Tool that wraps getBoundingClientRect with consistent output. */
  boundingClientRect: BoundingClientRectTool;

  /** Tool for parsing string-based values like '50%', '2rem', 'selfHeight'. */
  unitParser: UnitParserTool;

  /** Tool for performing linear interpolation (lerp). */
  lerp: LerpTool;

  /**
   * Tool for adaptive interpolation based on dynamic input value.
   * Useful when smoothing cursor speed, scroll velocity, etc.
   */
  adaptiveLerp: AdaptiveLerpTool;

  /**
   * Tool for parsing origin strings.
   * Supports values like `'top'`, `'center'`, or random expressions like `'random(top, bottom)'`.
   */
  originParser: OriginParserTool;

  /**
   * Tool for parsing CSS color strings into { r, g, b, a } format.
   * Supports `#hex`, `rgb[a](...)`, `hsl[a](...)` inputs.
   */
  colorParser: ColorParserTool;

  /**
   * Tool for validating strings using rules like `required`, `minLength`, `email`, etc.
   * Returns validation status, error code, and optional message.
   */
  validation: ValidationTool;

  /**
   * Tool for parsing CSS-like easing strings into easing functions.
   * Supports keywords like `'ease'`, `'linear'`, and full `cubic-bezier(...)` expressions.
   */
  easingFunction: EasingFunctionTool;

  /**
   * Tool for calculating magnetic offset strength based on proximity to pointer.
   */
  magneticPull: MagneticPullTool;

  /**
   * Tool for interpolating between two RGBA colors.
   * Accepts `from` and `to` colors as `{ r, g, b, a }`, and a `progress` value from `0` to `1`.
   * Returns an interpolated `StringColor` object.
   */
  lerpColor: LerpColorTool;

  /**
   * Tool for interpolating between two 2D vectors.
   * Accepts `{ x, y }` objects and a `progress` value between `0` and `1`.
   * Returns a new `{ x, y }` vector.
   */
  lerpVector: LerpVector2Tool;

  transformScaleParser: TransformScaleParserTool;

  optionsParser: SplitOptionsParserTool;

  ruleParser: RuleParserTool;
  styleTxn: typeof styleTxn;
}

/**
 * Default container holding references to internally implemented tools.
 * Modules can replace these with custom instances via setter methods.
 */
export class DefaultToolsContainer implements StringToolsContainer {
  public domAttribute = new DOMAttributeTool();
  public recordAttribute = new RecordAttributeTool();
  public transformNullify = new TransformNullifyTool();
  public boundingClientRect = new BoundingClientRectTool();
  public relativePosition = new RelativePositionTool(this.transformNullify);
  public unitParser = new UnitParserTool();
  public lerp = new LerpTool();
  public adaptiveLerp = new AdaptiveLerpTool();
  public originParser = new OriginParserTool();
  public colorParser = new ColorParserTool();
  public validation = new ValidationTool();
  public easingFunction = new EasingFunctionTool();
  public magneticPull = new MagneticPullTool();
  public lerpColor = new LerpColorTool();
  public lerpVector = new LerpVector2Tool();
  public transformScaleParser = new TransformScaleParserTool();
  public optionsParser = new SplitOptionsParserTool();
  public ruleParser = new RuleParserTool();
  public styleTxn = styleTxn;
}
